import { PersistentErrorBanner } from "@/components/persistent-error-banner";
import { useActiveEntity } from "@/lib/entity-context";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Shell-level banner for the two states where no entity can be resolved.
 *
 * OCT-10873 made entity-scoped reads fail closed: with no `X-Octa-Entity-Id`
 * header, a hardened list answers an empty 200 rather than widening to the
 * whole workspace. That is the right server policy, and the middleware logs
 * it - but only server-side. On the client the result is a fully-rendered app
 * where every list is empty, which looks exactly like a new workspace with no
 * data. `staleTime: Infinity` then holds those empty answers for the rest of
 * the session, and a 4xx on `/api/entities` is deliberately not retried, so
 * nothing recovers until the user reloads by chance.
 *
 * Two distinct states, deliberately worded differently:
 *
 * - `entitiesLoadFailed` - something broke. Say so, and offer a retry that
 *   also drops the empty caches.
 * - `hasNoSelectableEntity` - nothing broke; the workspace has no entity to
 *   scope to (empty, or every entity archived). Empty lists are the truthful
 *   answer here, so this is informational, not an error.
 *
 * @spec DOMAIN D23
 */
export function EntityScopeBanner() {
  const { t } = useTranslation("common");
  const { entitiesLoadFailed, entitiesRetryPending, hasNoSelectableEntity, retryEntitiesLoad } = useActiveEntity();

  if (entitiesLoadFailed) {
    return (
      <PersistentErrorBanner
        title={t("entityScopeBanner.loadFailedTitle")}
        description={t("entityScopeBanner.loadFailedDescription")}
        onRetry={retryEntitiesLoad}
        retryPending={entitiesRetryPending}
        retryLabel={t("entityScopeBanner.retry")}
        className="mb-2"
      />
    );
  }

  if (hasNoSelectableEntity) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs sm:text-sm"
        role="status"
        data-testid="banner-no-selectable-entity"
      >
        <Info className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground" data-testid="text-no-selectable-entity">
          {t("entityScopeBanner.noSelectableEntity")}
        </span>
      </div>
    );
  }

  return null;
}
